import { useRef, useState, KeyboardEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { ButtonV2 } from '@medeloop/medeloop-components-library';

import { safeRedirect } from 'src/utils/safe-redirect';
import { useTranslation } from 'react-i18next';
import { verifyUserAttribute } from 'src/api/users';
import { selectAccount, selectAuthToken } from 'src/store/selectors';
import { useSelector } from 'react-redux';

export function VerifyPhoneElement() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const token = useSelector(selectAuthToken);
  const { accessToken } = useSelector(selectAccount);

  const { destination } = location.state || { destination: '' };

  if (!destination) {
    navigate('/' + location.search);
  }

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const inputsRef = useRef<HTMLInputElement[]>([]);

  const [isSuccessful, setIsSuccessful] = useState(false);

  const { isPending, mutate } = useMutation({
    mutationFn: async () => {
      // Keeps the blocking screen if successful
      setIsSuccessful(true);

      const verifyNumberResponse = await verifyUserAttribute({
        code: code.join(''),
        attributeName: 'phone_number',
        accessToken: accessToken,
        token: token,
      });

      return verifyNumberResponse;
    },
    onSuccess: async (data) => {
      setError('');

      if (data) {
        let redirectUrl = new URLSearchParams(location.search).get('redirect_url');
        if (!redirectUrl) {
          // if no redirect url in the query params, use the one from local storage. SSO saves the redirect url in local storage
          redirectUrl = localStorage.getItem('redirect_url');
        }

        safeRedirect(redirectUrl);
      } else {
        setError(t('something_wrong_authentication'));
      }
    },
    onError: () => {
      setIsSuccessful(false);
      setError(t('code_inserted_wrong'));
    },
  });

  const handleRespondToVerificationCode = () => {
    mutate();
  };

  const handleVerificationCode = (val: string, idx: number) => {
    if (!isNaN(Number(val))) {
      const auxCode = [...code];
      auxCode[idx] = val;
      setCode(auxCode);

      /*
        If the index of the input array you are currently on is
        less than 5 and the next input exists, automatically focus
        the next input once you write a number.
      */
      if (val && idx < 5 && inputsRef.current[idx + 1]) {
        inputsRef.current[idx + 1].focus();
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, idx: number) => {
    if (e.key === 'Backspace' && !code[idx] && idx > 0) {
      inputsRef.current[idx - 1].focus();
    }

    if (e.key === 'Enter' && idx === code.length - 1 && !isPending) {
      handleRespondToVerificationCode();
    }
  };

  return (
    <div className="flex flex-col  gap-6 w-[456px] p-6 text-white relative">
      {(isPending || isSuccessful) && (
        <div className="absolute bg-dark-subdued-background w-full h-full opacity-50" />
      )}
      <div className="flex w-full flex-col gap-2 items-center">
        <h1 className="text-white text-4xl font-semibold text-center">
          {t('create_your_account')}
        </h1>
        <span className="text-center text-gray-400">
          {t('enter_code_to_destination', { destination })}
        </span>
      </div>

      <div className="flex w-full justify-between gap-2">
        {code.map((digit, idx) => (
          <input
            key={idx}
            className="flex p-4 text-4xl w-14 h-14 border bg-purple-gray-900 border-gray-600 focus:outline-none rounded-2xl focus:border-cyan-300"
            type="text"
            maxLength={1}
            value={digit}
            onChange={(e) => handleVerificationCode(e.target.value, idx)}
            onKeyDown={(e) => handleKeyDown(e, idx)}
            ref={(el) => {
              if (el) {
                inputsRef.current[idx] = el;
              }
            }}
            autoFocus={idx === 0}
          />
        ))}
      </div>
      {error && <span className="text-red-500 text-sm">{error}</span>}

      <ButtonV2
        variant="primary"
        style={{ width: '100%' }}
        disabled={isPending}
        onClick={handleRespondToVerificationCode}
      >
        {t('continue')}
      </ButtonV2>
    </div>
  );
}
